import React, { useState } from 'react';
import { X, Plus, Upload, Sparkles, BookOpen, Edit2, Utensils, Zap, ChefHat, ArrowLeft, Check, Trash2, Settings2, ListPlus, LayoutGrid, Type, DollarSign, AlignLeft, AlertTriangle } from 'lucide-react';
import { Modal, ModalHeader, ModalBody, Button } from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { useMenuItemsStore } from '../../../stores/Assets/menuItems';
import { useOptionStore } from '../../../stores/Assets/ItemOptions';
import { toast } from 'react-toastify';

const THEME_COLOR = '#c59253';
const FAST_TRANSITION = { type: 'spring', damping: 30, stiffness: 500 };

const CURRENCIES = [

    { code: 'QAR', symbol: 'QR', name: 'Qatari Riyal' },
    { code: 'DZD', symbol: 'DZD', name: 'Algerian Dinar' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'SAR', symbol: '⃁', name: 'Saudi Riyal' },
    { code: 'AED', symbol: 'AED', name: 'UAE Dirham' },
];


const InputContainer = ({ label, children, icon: Icon, error, compact = false }) => (
    <div className={`flex-grow-1 ${compact ? 'mb-2' : 'mb-3'}`}>
        <div className="d-flex align-items-center justify-content-between mb-1 opacity-75">
            <label className="small fw-bold text-uppercase text-muted ps-1" style={{ fontSize: '9px', letterSpacing: '1px' }}>
                {label}
            </label>
        </div>
        <div
            className={`d-flex align-items-center gap-2 px-3 py-2 bg-white rounded-4 border-2 transition-all shadow-sm ${error ? 'border-danger' : 'border-light-subtle hover-border-primary'}`}
            style={{
                minHeight: '42px',
                borderColor: '#eee'
            }}
        >
            {Icon && <Icon size={14} className="text-secondary opacity-50" />}
            <div className="flex-grow-1">{children}</div>
        </div>
    </div>
);

const Toggle = ({ active, onToggle, label }) => (
    <div className="d-flex align-items-center gap-2 cursor-pointer" onClick={onToggle}>
        <div
            className="position-relative rounded-pill transition-all"
            style={{
                width: '32px',
                height: '18px',
                backgroundColor: active ? THEME_COLOR : '#dee2e6',
                cursor: 'pointer'
            }}
        >
            <motion.div
                animate={{ x: active ? 14 : 2 }}
                initial={false}
                className="position-absolute bg-white rounded-circle shadow-sm"
                style={{ width: '14px', height: '14px', top: '2px' }}
            />
        </div>
        {label && <span className="small fw-bold text-muted" style={{ fontSize: '10px' }}>{label}</span>}
    </div>
);

const OptionsSection = ({ options = [], onAdd, onRemove, onUpdate, onAddChoice, onUpdateChoice, onRemoveChoice, setView, selectedCatalogItem, currencySymbol, setHasChanges }) => (
    <div className="mt-4 pt-4 border-top">
        <div className="d-flex align-items-center justify-content-between mb-3 px-1">
            <div className="d-flex align-items-center gap-2">
                <div className="p-1 rounded-2 bg-primary bg-opacity-10">
                    <ListPlus size={14} className="text-primary" />
                </div>
                <label className="mb-0 small fw-bold text-uppercase text-dark opacity-75" style={{ fontSize: '10px', letterSpacing: '1px' }}>
                    Add-ons & Variations
                </label>
            </div>
            <div className="d-flex gap-2">
                <button
                    onClick={() => setView(selectedCatalogItem ? 'catalog-options-catalog' : 'options-catalog')}
                    className="btn btn-sm btn-white border-light shadow-sm rounded-pill px-3 py-1 d-flex align-items-center gap-1 transition-all hover-shadow"
                    style={{ fontSize: '10px', fontWeight: '700' }}
                >
                    <BookOpen size={12} className="text-primary opacity-75" /> Catalog
                </button>
                <button
                    onClick={onAdd}
                    className="btn btn-sm btn-primary shadow-sm rounded-pill px-3 py-1 d-flex align-items-center gap-1 transition-all"
                    style={{ fontSize: '10px', fontWeight: '700', backgroundColor: THEME_COLOR, borderColor: THEME_COLOR }}
                >
                    <Plus size={12} /> Custom
                </button>
            </div>
        </div>

        <div className="d-flex flex-column gap-2">
            <AnimatePresence initial={false}>
                {options.map((opt, optIdx) => (
                    <motion.div
                        key={optIdx}
                        layout
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        transition={FAST_TRANSITION}
                        className="p-3 bg-light bg-opacity-50 rounded-4 border shadow-none position-relative"
                    >
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <input
                                className="form-control form-control-sm border-2 bg-white fw-bold px-2 rounded-3 shadow-none"
                                style={{ fontSize: '0.85rem' }}
                                placeholder="Group Name (e.g. Size)"
                                value={opt.name}
                                onChange={(e) => { onUpdate(optIdx, e.target.value); setHasChanges(true); }}
                            />
                            <button
                                onClick={() => onRemove(optIdx)}
                                className="btn btn-link text-muted p-1"
                            >
                                <Trash2 size={12} />
                            </button>
                        </div>

                        <div className="d-flex flex-column gap-2">
                            {opt.choices.map((choice, choiceIdx) => (
                                <div
                                    key={choiceIdx}
                                    className="d-flex align-items-center gap-2 bg-white p-1 rounded-3 px-2 border border-light"
                                >
                                    <input
                                        className="form-control form-control-sm border-0 bg-transparent p-0 shadow-none fw-medium flex-grow-1"
                                        style={{ fontSize: '0.8rem' }}
                                        placeholder="Choice"
                                        value={choice.name}
                                        onChange={(e) => onUpdateChoice(optIdx, choiceIdx, 'name', e.target.value)}
                                    />
                                    <div className="d-flex align-items-center gap-1 border-start ps-2" style={{ width: '80px' }}>
                                        <span className="small text-muted" style={{ fontSize: '10px' }}>{currencySymbol}</span>
                                        <input
                                            className="form-control form-control-sm border-0 bg-transparent p-0 shadow-none text-end fw-bold"
                                            style={{ color: THEME_COLOR, fontSize: '0.8rem' }}
                                            placeholder="0.00"
                                            value={choice.price}
                                            onChange={(e) => onUpdateChoice(optIdx, choiceIdx, 'price', e.target.value)}
                                        />
                                    </div>
                                    <div className="border-start ps-2">
                                        <Toggle
                                            active={choice.isAvailable !== false}
                                            onToggle={() => onUpdateChoice(optIdx, choiceIdx, 'isAvailable', choice.isAvailable === false)}
                                        />
                                    </div>
                                    <button
                                        onClick={() => onRemoveChoice(optIdx, choiceIdx)}
                                        className="btn btn-link text-muted p-1"
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                            ))}
                            <button
                                onClick={() => onAddChoice(optIdx)}
                                className="btn btn-link btn-sm text-decoration-none p-0 text-muted d-flex align-items-center gap-1 mt-1"
                                style={{ fontSize: '10px', fontWeight: '700' }}
                            >
                                <Plus size={10} /> Add Choice
                            </button>
                        </div>
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    </div>
);

const ItemTypeCardCompact = ({ type, current, onSelect, title, icon: Icon, description }) => (
    <button
        onClick={() => onSelect(type)}
        className={`flex-grow-1 text-start p-2 border-0 rounded-4 transition-all d-flex gap-2 position-relative overflow-hidden ${current === type ? 'shadow-sm' : ''}`}
        style={{
            backgroundColor: current === type ? THEME_COLOR : '#f8f9fa',
            minWidth: '120px',
        }}
    >
        <div
            className={`rounded-3 d-flex align-items-center justify-content-center transition-all ${current === type ? 'bg-white' : 'bg-light'}`}
            style={{ width: '32px', height: '32px', flexShrink: 0 }}
        >
            <Icon size={16} color={current === type ? THEME_COLOR : '#666'} />
        </div>
        <div className="flex-grow-1 overflow-hidden">
            <span className={`small fw-bold d-block ${current === type ? 'text-white' : 'text-dark'}`} style={{ fontSize: '10px', letterSpacing: '0.5px' }}>{title}</span>
            <span className={`text-truncate d-block`} style={{ fontSize: '9px', color: current === type ? 'rgba(255,255,255,0.8)' : '#888' }}>
                {description}
            </span>
        </div>
    </button>
);


const AddItemDialog = ({ isOpen, onClose, onAdd, businessType, currency = 'USD', itemToEdit = null }) => {
    const [view, setView] = useState('options'); // options, manual, catalog, options-catalog
    const [hasChanges, setHasChanges] = useState(false);
    const [showExitConfirm, setShowExitConfirm] = useState(false);

    React.useEffect(() => {
        setHasChanges(false);
    }, [isOpen]);

    const { catalog, getCatalog, isLoading, allTags, fetchAllTags } = useMenuItemsStore(state => state);
    const { defaultOptions, getDefaultOptions } = useOptionStore(state => state);

    React.useEffect(() => {
        if (isOpen) {
            getCatalog();
            getDefaultOptions();
            fetchAllTags();
            if (itemToEdit) {
                setFormData({
                    ...itemToEdit,
                    tags: itemToEdit.tags || [],
                    imagePreview: itemToEdit.imagePreview || null,
                    options: itemToEdit.options || [],
                    promo_price: itemToEdit.promo_price || '',
                    is_promo: itemToEdit.is_promo ?? false,
                    isHidden: itemToEdit.isHidden ?? false,
                    isListed: itemToEdit.isListed ?? true,
                    available: itemToEdit.available ?? true,
                });
                setView('manual');
            } else {
                resetContext();
            }
        }
    }, [isOpen, itemToEdit]);

    const [formData, setFormData] = useState({
        name: '',
        name_ar: '',
        description: '',
        description_ar: '',
        price: '',
        promo_price: '',
        is_promo: false,
        isHidden: false,
        isListed: true,
        available: true,
        tags: [],
        imageFile: null,
        imagePreview: null,
        type: businessType === 'beach' ? 'service' : 'default',
        options: []
    });
    const [tagInput, setTagInput] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedCatalogItem, setSelectedCatalogItem] = useState(null);

    const catalogItems = catalog || [];
    const optionsCatalog = (defaultOptions || []).map(opt => ({
        id: opt.menu_item_option_id,
        name: opt.name,
        choices: (opt.choices || []).map(c => ({
            name: c.name,
            price: c.price || '0',
            isAvailable: true
        }))
    }));

    const isBeach = businessType === 'beach';
    const showTypeSelector = (businessType === 'restaurant' || businessType === 'office') && !itemToEdit;
    const currencySymbol = CURRENCIES.find(c => c.code === currency)?.code || '$';

    const validateItem = (item) => {
        if (!item.name?.trim()) {
            toast.error("Item name is required");
            return false;
        }

        if (item.options && item.options.length > 0) {
            for (let i = 0; i < item.options.length; i++) {
                const opt = item.options[i];
                if (!opt.name?.trim()) {
                    toast.error(`Variation group #${i + 1} needs a name (e.g., Size, Toppings)`);
                    return false;
                }

                if (!opt.choices || opt.choices.length === 0) {
                    toast.error(`Variation group "${opt.name}" needs at least one choice`);
                    return false;
                }

                for (let j = 0; j < opt.choices.length; j++) {
                    const choice = opt.choices[j];
                    if (!choice.name?.trim()) {
                        toast.error(`Choice #${j + 1} in "${opt.name}" needs a name`);
                        return false;
                    }
                }
            }
        }
        return true;
    };

    const handleManualAdd = () => {
        if (!validateItem(formData)) return;
        onAdd(formData);
        resetContext();
        setHasChanges(false);
        onClose();
    };

    const handleCatalogAdd = () => {
        if (!selectedCatalogItem) return;
        if (!validateItem(selectedCatalogItem)) return;
        onAdd(selectedCatalogItem);
        resetContext();
        setHasChanges(false);
        onClose();
    };

    const attemptClose = () => {
        if (hasChanges) {
            setShowExitConfirm(true);
        } else {
            onClose();
        }
    };

    const resetContext = () => {
        setView('options');
        setFormData({
            name: '',
            name_ar: '',
            description: '',
            description_ar: '',
            price: '',
            promo_price: '',
            is_promo: false,
            isHidden: false,
            isListed: true,
            available: true,
            tags: [],
            imageFile: null,
            imagePreview: null,
            type: isBeach ? 'service' : 'default',
            options: []
        });
        setTagInput('');
        setSelectedCatalogItem(null);
        setHasChanges(false);
    };

    const addTag = (tagToForce = null) => {
        const tagToAdd = (tagToForce || tagInput).trim().replace(/,$/, '');
        if (tagToAdd) {
            const currentTags = selectedCatalogItem?.tags || formData.tags;
            if (!currentTags.includes(tagToAdd)) {
                setHasChanges(true);
                const newTags = [...currentTags, tagToAdd];
                selectedCatalogItem ? setSelectedCatalogItem({ ...selectedCatalogItem, tags: newTags }) : setFormData({ ...formData, tags: newTags });
            }
            setTagInput('');
            setShowSuggestions(false);
        }
    };

    const handleImageUpload = (e, target) => {
        const file = e.target.files?.[0];
        if (file) {
            setHasChanges(true);
            const previewUrl = URL.createObjectURL(file);
            if (target === 'form') {
                setFormData({ ...formData, imageFile: file, imagePreview: previewUrl });
            } else if (target === 'catalog' && selectedCatalogItem) {
                setSelectedCatalogItem({ ...selectedCatalogItem, imageFile: file, imagePreview: previewUrl });
            }
        }
    };

    const addOption = () => {
        setHasChanges(true);
        const newOption = { name: '', choices: [{ name: '', price: '', isAvailable: true }] };
        if (selectedCatalogItem) {
            setSelectedCatalogItem({ ...selectedCatalogItem, options: [...(selectedCatalogItem.options || []), newOption] });
        } else {
            setFormData({ ...formData, options: [...formData.options, newOption] });
        }
    };

    const removeOption = (index) => {
        setHasChanges(true);
        if (selectedCatalogItem) {
            const newOptions = [...selectedCatalogItem.options];
            newOptions.splice(index, 1);
            setSelectedCatalogItem({ ...selectedCatalogItem, options: newOptions });
        } else {
            const newOptions = [...formData.options];
            newOptions.splice(index, 1);
            setFormData({ ...formData, options: newOptions });
        }
    };

    const updateOptionName = (index, name) => {
        setHasChanges(true);
        if (selectedCatalogItem) {
            const newOptions = [...selectedCatalogItem.options];
            newOptions[index].name = name;
            setSelectedCatalogItem({ ...selectedCatalogItem, options: newOptions });
        } else {
            const newOptions = [...formData.options];
            newOptions[index].name = name;
            setFormData({ ...formData, options: newOptions });
        }
    };

    const addChoice = (optIndex) => {
        setHasChanges(true);
        if (selectedCatalogItem) {
            const newOptions = [...selectedCatalogItem.options];
            newOptions[optIndex].choices.push({ name: '', price: '', isAvailable: true });
            setSelectedCatalogItem({ ...selectedCatalogItem, options: newOptions });
        } else {
            const newOptions = [...formData.options];
            newOptions[optIndex].choices.push({ name: '', price: '', isAvailable: true });
            setFormData({ ...formData, options: newOptions });
        }
    };

    const updateChoice = (optIndex, choiceIndex, field, value) => {
        setHasChanges(true);
        if (selectedCatalogItem) {
            const newOptions = [...selectedCatalogItem.options];
            newOptions[optIndex].choices[choiceIndex][field] = value;
            setSelectedCatalogItem({ ...selectedCatalogItem, options: newOptions });
        } else {
            const newOptions = [...formData.options];
            newOptions[optIndex].choices[choiceIndex][field] = value;
            setFormData({ ...formData, options: newOptions });
        }
    };

    const removeChoice = (optIndex, choiceIndex) => {
        setHasChanges(true);
        if (selectedCatalogItem) {
            const newOptions = [...selectedCatalogItem.options];
            newOptions[optIndex].choices.splice(choiceIndex, 1);
            setSelectedCatalogItem({ ...selectedCatalogItem, options: newOptions });
        } else {
            const newOptions = [...formData.options];
            newOptions[optIndex].choices.splice(choiceIndex, 1);
            setFormData({ ...formData, options: newOptions });
        }
    };

    const applyCatalogOption = (option) => {
        setHasChanges(true);
        const optionWithId = { ...option, choices: option.choices.map(c => ({ ...c })) };
        if (selectedCatalogItem) {
            setSelectedCatalogItem({ ...selectedCatalogItem, options: [...(selectedCatalogItem.options || []), optionWithId] });
        } else {
            setFormData({ ...formData, options: [...formData.options, optionWithId] });
        }
        setView(selectedCatalogItem ? 'catalog-detail' : 'manual');
    };

    return (
        <Modal isOpen={isOpen} toggle={attemptClose} size="lg" centered className="premium-modal">
            <ModalHeader className="border-0 px-4 pt-3 pb-0 w-100">
                <div className="d-flex align-items-center gap-3">
                    <button onClick={attemptClose} className="btn btn-light rounded-circle p-2 hover-bg-silver text-muted border">
                        <ArrowLeft size={20} />
                    </button>
                    <div className="bg-light p-2 rounded-3">
                        {view.startsWith('options') ? <LayoutGrid size={18} color={THEME_COLOR} /> :
                            view === 'catalog' ? <BookOpen size={18} color={THEME_COLOR} /> :
                                <Edit2 size={18} color={THEME_COLOR} />}
                    </div>
                    <div>
                        <h6 className="fw-bold mb-0">
                            {itemToEdit ? 'Edit Item' :
                                view.startsWith('manual') ? 'Custom Item' :
                                    view.startsWith('catalog-detail') ? 'Catalog Item' :
                                        view === 'catalog' ? 'Browse Catalog' : 'Add New Item'}
                        </h6>
                    </div>
                </div>
            </ModalHeader>

            <ModalBody className="p-4 pt-2">
                <AnimatePresence mode="wait">
                    {view === 'options' && (
                        <motion.div
                            key="options"
                            initial={{ opacity: 0, scale: 0.98 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.98 }}
                            transition={FAST_TRANSITION}
                            className="row g-3 py-2"
                        >
                            <div className="col-12 col-sm-6">
                                <motion.div
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => setView('manual')}
                                    className="w-100 p-4 rounded-4 border bg-white shadow-sm text-center cursor-pointer transition-all"
                                >
                                    <div className="bg-light d-inline-flex p-3 rounded-circle mb-3" style={{ color: THEME_COLOR }}>
                                        <Plus size={24} strokeWidth={2.5} />
                                    </div>
                                    <h6 className="fw-bold mb-1">Manual Entry</h6>
                                    <p className="text-muted small mb-0 x-small" style={{ fontSize: '10px' }}>Custom item with unique details</p>
                                </motion.div>
                            </div>
                            <div className="col-12 col-sm-6">
                                <motion.div
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => setView('catalog')}
                                    className="w-100 p-4 rounded-4 border bg-white shadow-sm text-center cursor-pointer transition-all"
                                >
                                    <div className="bg-light d-inline-flex p-3 rounded-circle mb-3" style={{ color: '#333' }}>
                                        <BookOpen size={24} strokeWidth={2.5} />
                                    </div>
                                    <h6 className="fw-bold mb-1">From Our Catalog</h6>
                                    <p className="text-muted small mb-0 x-small" style={{ fontSize: '10px' }}>Quickly add from industry database</p>
                                </motion.div>
                            </div>
                        </motion.div>
                    )}

                    {(view === 'manual' || view === 'catalog-detail') && (
                        <motion.div
                            key="form"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={FAST_TRANSITION}
                            className="d-flex flex-column gap-2"
                        >
                            {!itemToEdit && (
                                <button onClick={() => setView(selectedCatalogItem ? 'catalog' : 'options')} className="btn btn-link text-start p-0 text-muted x-small text-decoration-none d-inline-flex align-items-center gap-1 mb-1 fw-bold transition-all" style={{ fontSize: '10px' }}>
                                    <ArrowLeft size={12} /> GO BACK
                                </button>
                            )}

                            <div className="row g-3 align-items-start">
                                <div className="col-auto">
                                    <div className="position-relative" style={{ width: '80px', height: '80px' }}>
                                        <div className="rounded-3 overflow-hidden shadow-sm border h-100 w-100 bg-light">
                                            {(selectedCatalogItem?.imagePreview || formData.imagePreview) ? (
                                                <img src={selectedCatalogItem?.imagePreview || formData.imagePreview} className="object-fit-cover w-100 h-100" alt="Item" />
                                            ) : (
                                                <div className="d-flex align-items-center justify-content-center h-100 opacity-20">
                                                    <Upload size={24} />
                                                </div>
                                            )}
                                        </div>
                                        <label
                                            htmlFor="item-image-upload"
                                            className="position-absolute bottom-0 end-0 bg-white shadow rounded-circle d-flex align-items-center justify-content-center cursor-pointer border"
                                            style={{ width: '24px', height: '24px', marginBottom: '-4px', marginRight: '-4px' }}
                                        >
                                            <Plus size={12} color={THEME_COLOR} />
                                        </label>
                                        <input
                                            id="item-image-upload"
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleImageUpload(e, selectedCatalogItem ? 'catalog' : 'form')}
                                            className="d-none"
                                        />
                                    </div>
                                </div>

                                <div className="col">
                                    <div className="row g-2 mb-2">
                                        <div className="col-12 col-md-6">
                                            <label className="x-small fw-bold text-uppercase text-muted d-flex justify-content-between mb-1" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                                                Item Name (EN) <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                className="form-control form-control-sm border-2 shadow-none rounded-3"
                                                placeholder="e.g. Classic Burger"
                                                value={selectedCatalogItem?.name || formData.name}
                                                onChange={(e) => { setHasChanges(true); selectedCatalogItem ? setSelectedCatalogItem({ ...selectedCatalogItem, name: e.target.value }) : setFormData({ ...formData, name: e.target.value }) }}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="x-small fw-bold text-uppercase text-muted d-flex justify-content-between mb-1" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                                                Name (AR) <span className="text-lowercase opacity-50 fw-normal">Optional</span>
                                            </label>
                                            <input
                                                dir="rtl"
                                                className="form-control form-control-sm border-2 shadow-none rounded-3"
                                                placeholder="مثال: برجر كلاسيك"
                                                value={selectedCatalogItem?.name_ar || formData.name_ar}
                                                onChange={(e) => { setHasChanges(true); selectedCatalogItem ? setSelectedCatalogItem({ ...selectedCatalogItem, name_ar: e.target.value }) : setFormData({ ...formData, name_ar: e.target.value }) }}
                                            />
                                        </div>
                                    </div>

                                    <div className="row g-2 mb-2">
                                        <div className="col-12 col-md-6">
                                            <label className="x-small fw-bold text-uppercase text-muted d-block mb-1" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>Description (EN)</label>
                                            <textarea
                                                className="form-control form-control-sm border-2 shadow-none rounded-3"
                                                rows="2"
                                                placeholder="Brief description..."
                                                value={selectedCatalogItem?.description || formData.description}
                                                onChange={(e) => { setHasChanges(true); selectedCatalogItem ? setSelectedCatalogItem({ ...selectedCatalogItem, description: e.target.value }) : setFormData({ ...formData, description: e.target.value }) }}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="x-small fw-bold text-uppercase text-muted d-block mb-1 text-end" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>Description (AR)</label>
                                            <textarea
                                                dir="rtl"
                                                className="form-control form-control-sm border-2 shadow-none rounded-3 text-end"
                                                rows="2"
                                                placeholder="وصف بسيط..."
                                                value={selectedCatalogItem?.description_ar || formData.description_ar}
                                                onChange={(e) => { setHasChanges(true); selectedCatalogItem ? setSelectedCatalogItem({ ...selectedCatalogItem, description_ar: e.target.value }) : setFormData({ ...formData, description_ar: e.target.value }) }}
                                            />
                                        </div>
                                    </div>

                                    <div className="row g-2 mb-2">
                                        <div className="col-12 col-md-6">
                                            <label className="x-small fw-bold text-uppercase text-muted d-block mb-1" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>Price ({currencySymbol})</label>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm border-2 shadow-none rounded-3 fw-bold"
                                                style={{ color: THEME_COLOR }}
                                                placeholder="0.00"
                                                value={selectedCatalogItem?.price || formData.price}
                                                onChange={(e) => { setHasChanges(true); selectedCatalogItem ? setSelectedCatalogItem({ ...selectedCatalogItem, price: e.target.value }) : setFormData({ ...formData, price: e.target.value }) }}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="x-small fw-bold text-uppercase text-muted d-block mb-1" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>Promo Price ({currencySymbol})</label>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm border-2 shadow-none rounded-3 fw-bold"
                                                style={{ color: '#e74c3c' }}
                                                placeholder="0.00"
                                                value={selectedCatalogItem?.promo_price || formData.promo_price}
                                                onChange={(e) => { setHasChanges(true); selectedCatalogItem ? setSelectedCatalogItem({ ...selectedCatalogItem, promo_price: e.target.value }) : setFormData({ ...formData, promo_price: e.target.value }) }}
                                            />
                                        </div>
                                    </div>

                                    <div className="row g-2 mb-2">
                                        <div className="col-12">
                                            <label className="x-small fw-bold text-uppercase text-muted d-block mb-1" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>Tags</label>
                                            <div className="d-flex flex-wrap gap-2 p-2 border-2 bg-light rounded-3 min-vh-0" style={{ minHeight: '38px' }}>
                                                {(selectedCatalogItem?.tags || formData.tags).map((tag, i) => (
                                                    <span key={i} className="badge bg-white text-dark border rounded-pill px-2 py-1 d-flex align-items-center gap-1 shadow-xs" style={{ fontSize: '9px' }}>
                                                        {tag}
                                                        <X size={10} className="cursor-pointer text-danger" onClick={() => {
                                                            setHasChanges(true);
                                                            const newTags = (selectedCatalogItem?.tags || formData.tags).filter((_, idx) => idx !== i);
                                                            selectedCatalogItem ? setSelectedCatalogItem({ ...selectedCatalogItem, tags: newTags }) : setFormData({ ...formData, tags: newTags });
                                                        }} />
                                                    </span>
                                                ))}
                                                <div className="position-relative flex-grow-1 d-flex">
                                                    <input
                                                        className="border-0 bg-transparent flex-grow-1 shadow-none x-small"
                                                        style={{ fontSize: '11px', outline: 'none' }}
                                                        placeholder="E.g, Vegetarian, Spicy... "
                                                        value={tagInput}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            if (val.endsWith(',')) {
                                                                addTag(val);
                                                            } else {
                                                                setTagInput(val);
                                                                setShowSuggestions(true);
                                                            }
                                                        }}
                                                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                if (tagInput.trim()) {
                                                                    e.preventDefault();
                                                                    addTag();
                                                                }
                                                            }
                                                        }}
                                                    />
                                                    <AnimatePresence>
                                                        {showSuggestions && tagInput.trim() && (
                                                            <motion.div
                                                                initial={{ opacity: 0, y: -10 }}
                                                                animate={{ opacity: 1, y: 0 }}
                                                                exit={{ opacity: 0, y: -10 }}
                                                                className="position-absolute bg-white shadow-lg rounded-3 border overflow-hidden"
                                                                style={{ top: '100%', left: 0, right: 0, zIndex: 1000, marginTop: '8px' }}
                                                            >
                                                                <div className="d-flex flex-column" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                                                    {allTags
                                                                        .filter(t => t.toLowerCase().includes(tagInput.toLowerCase()) && !(selectedCatalogItem?.tags || formData.tags).includes(t))
                                                                        .map((tag, idx) => (
                                                                            <button
                                                                                key={idx}
                                                                                className="btn btn-white text-start p-2 px-3 border-0 rounded-0 x-small hover-bg-light transition-all"
                                                                                style={{ fontSize: '11px' }}
                                                                                onClick={() => addTag(tag)}
                                                                            >
                                                                                {tag}
                                                                            </button>
                                                                        ))}
                                                                </div>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </div>
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        addTag();
                                                    }}
                                                    style={{ borderColor: THEME_COLOR }}
                                                    className="btn btn-link p-0  hover-opacity-100 transition-all shadow-none border d-flex align-items-center"
                                                >
                                                    <Plus size={14} style={{ color: THEME_COLOR, fontWeight: 'bold' }} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {showTypeSelector && (
                                        <div className="d-flex flex-column gap-1 mt-2">
                                            <label className="x-small fw-bold text-uppercase text-muted ps-1" style={{ fontSize: '9px', letterSpacing: '1px' }}>Classification</label>
                                            <div className="d-flex gap-2">
                                                <ItemTypeCardCompact
                                                    type="default"
                                                    current={selectedCatalogItem?.type || formData.type}
                                                    onSelect={(t) => { setHasChanges(true); selectedCatalogItem ? setSelectedCatalogItem({ ...selectedCatalogItem, type: t }) : setFormData({ ...formData, type: t }); }}
                                                    title="Standard"
                                                    description="Regular dish"
                                                    icon={Utensils}
                                                />
                                                <ItemTypeCardCompact
                                                    type="service"
                                                    current={selectedCatalogItem?.type || formData.type}
                                                    onSelect={(t) => { setHasChanges(true); selectedCatalogItem ? setSelectedCatalogItem({ ...selectedCatalogItem, type: t }) : setFormData({ ...formData, type: t }); }}
                                                    title="Service"
                                                    description="Requests"
                                                    icon={Zap}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <OptionsSection
                                        options={selectedCatalogItem?.options || formData.options}
                                        onAdd={addOption}
                                        onRemove={removeOption}
                                        onUpdate={updateOptionName}
                                        onAddChoice={addChoice}
                                        onUpdateChoice={updateChoice}
                                        onRemoveChoice={removeChoice}
                                        setView={setView}
                                        selectedCatalogItem={selectedCatalogItem}
                                        currencySymbol={currencySymbol}
                                        setHasChanges={setHasChanges}
                                    />

                                    <div className="mt-4 pt-4 border-top">
                                        <div className="d-flex align-items-center gap-2 mb-3 px-1">
                                            <div className="p-1 rounded-2 bg-primary bg-opacity-10">
                                                <Settings2 size={14} className="text-primary" />
                                            </div>
                                            <label className="mb-0 small fw-bold text-uppercase text-dark opacity-75" style={{ fontSize: '10px', letterSpacing: '1px' }}>
                                                Status & Visibility
                                            </label>
                                        </div>

                                        <div className="row g-3 px-1">
                                            <div className="col-12 col-sm-6">
                                                <Toggle
                                                    active={selectedCatalogItem ? selectedCatalogItem.available : formData.available}
                                                    onToggle={() => {
                                                        setHasChanges(true);
                                                        const newVal = selectedCatalogItem ? !selectedCatalogItem.available : !formData.available;
                                                        selectedCatalogItem ? setSelectedCatalogItem({ ...selectedCatalogItem, available: newVal }) : setFormData({ ...formData, available: newVal });
                                                    }}
                                                    label="Available"
                                                />
                                                <p className="text-muted mt-1 mb-0" style={{ fontSize: '9px', lineHeight: '1.2' }}>Turned Off when the item is out of stock.</p>
                                            </div>
                                            <div className="col-12 col-sm-6">
                                                <Toggle
                                                    active={selectedCatalogItem ? selectedCatalogItem.is_promo : formData.is_promo}
                                                    onToggle={() => {
                                                        setHasChanges(true);
                                                        const newVal = selectedCatalogItem ? !selectedCatalogItem.is_promo : !formData.is_promo;
                                                        selectedCatalogItem ? setSelectedCatalogItem({ ...selectedCatalogItem, is_promo: newVal }) : setFormData({ ...formData, is_promo: newVal });
                                                    }}
                                                    label="Promotion"
                                                />
                                                <p className="text-muted mt-1 mb-0" style={{ fontSize: '9px', lineHeight: '1.2' }}>Show in the top promo section.</p>
                                            </div>
                                            <div className="col-12 col-sm-6">
                                                <Toggle
                                                    active={selectedCatalogItem ? selectedCatalogItem.isHidden : formData.isHidden}
                                                    onToggle={() => {
                                                        setHasChanges(true);
                                                        const newVal = selectedCatalogItem ? !selectedCatalogItem.isHidden : !formData.isHidden;
                                                        selectedCatalogItem ? setSelectedCatalogItem({ ...selectedCatalogItem, isHidden: newVal }) : setFormData({ ...formData, isHidden: newVal });
                                                    }}
                                                    label="Hidden"
                                                />
                                                <p className="text-muted mt-1 mb-0" style={{ fontSize: '9px', lineHeight: '1.2' }}>Hide from the menu.</p>
                                            </div>
                                            <div className="col-12 col-sm-6">
                                                <Toggle
                                                    active={selectedCatalogItem ? selectedCatalogItem.isListed : formData.isListed}
                                                    onToggle={() => {
                                                        setHasChanges(true);
                                                        const newVal = selectedCatalogItem ? !selectedCatalogItem.isListed : !formData.isListed;
                                                        selectedCatalogItem ? setSelectedCatalogItem({ ...selectedCatalogItem, isListed: newVal }) : setFormData({ ...formData, isListed: newVal });
                                                    }}
                                                    label="Listed"
                                                />
                                                <p className="text-muted mt-1 mb-0" style={{ fontSize: '9px', lineHeight: '1.2' }}>Show this item for service station users.</p>
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        onClick={selectedCatalogItem ? handleCatalogAdd : handleManualAdd}
                                        className="btn text-white w-100 py-2 rounded-pill fw-bold mt-4 shadow-sm border-0 transition-all"
                                        style={{ backgroundColor: THEME_COLOR, fontSize: '0.85rem' }}
                                    >
                                        SAVE
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {view === 'catalog' && (
                        <motion.div
                            key="catalog"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={FAST_TRANSITION}
                        >
                            <button onClick={() => setView('options')} className="btn btn-link text-start p-0 text-muted x-small text-decoration-none d-inline-flex align-items-center gap-1 mb-3 fw-bold" style={{ fontSize: '10px' }}>
                                <ArrowLeft size={12} /> GO BACK
                            </button>
                            <div className="row g-2 overflow-auto" style={{ maxHeight: '400px' }}>
                                {isLoading ? (
                                    <div className="col-12 text-center py-5">
                                        <div className="spinner-border text-primary spinner-border-sm" role="status"></div>
                                        <p className="mt-2 text-muted x-small">Loading...</p>
                                    </div>
                                ) : catalogItems.map((item, idx) => (
                                    <div key={idx} className="col-12 col-sm-6">
                                        <motion.button
                                            whileHover={{ scale: 1.01 }}
                                            whileTap={{ scale: 0.99 }}
                                            onClick={() => {
                                                setSelectedCatalogItem({
                                                    ...item,
                                                    name: item.title,
                                                    image_url: item.image_url,
                                                    imagePreview: item.image_url,
                                                    tags: item.tags || [],
                                                    promo_price: item.promo_price || '',
                                                    is_promo: item.is_promo ?? false,
                                                    isHidden: item.isHidden ?? false,
                                                    isListed: item.isListed ?? true,
                                                    available: item.available ?? true,
                                                });
                                                setView('catalog-detail');
                                            }}
                                            className="w-100 text-start border-0 p-2 rounded-3 bg-white shadow-sm h-100 transition-all border d-flex gap-2"
                                        >
                                            {item.image_url && (
                                                <div className="rounded-2 overflow-hidden flex-shrink-0" style={{ width: '50px', height: '50px' }}>
                                                    <img src={item.image_url} alt={item.title} className="w-100 h-100 object-fit-cover" />
                                                </div>
                                            )}
                                            <div className="flex-grow-1 min-w-0">
                                                <h6 className="fw-bold mb-0 text-truncate" style={{ fontSize: '0.8rem' }}>{item.title}</h6>
                                                <p className="x-small text-muted mb-0 text-truncate" style={{ fontSize: '10px' }}>{item.description}</p>
                                                <div className="d-flex align-items-center justify-content-between mt-1">
                                                    <span className="fw-bold x-small" style={{ color: THEME_COLOR }}>{currencySymbol}{item.price || '0'}</span>
                                                    <Plus size={12} className="text-primary" />
                                                </div>
                                            </div>
                                        </motion.button>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    )}

                    {(view === 'options-catalog' || view === 'catalog-options-catalog') && (
                        <motion.div
                            key="options-catalog"
                            initial={{ opacity: 0, scale: 0.98 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.98 }}
                            transition={FAST_TRANSITION}
                        >
                            <button onClick={() => setView(selectedCatalogItem ? 'catalog-detail' : 'manual')} className="btn btn-link text-start p-0 text-muted x-small text-decoration-none d-inline-flex align-items-center gap-1 mb-3 fw-bold" style={{ fontSize: '10px' }}>
                                <ArrowLeft size={12} /> BACK
                            </button>
                            <h6 className="fw-bold mb-3">Quick Add Group</h6>
                            <div className="row g-2 overflow-auto" style={{ maxHeight: '400px' }}>
                                {optionsCatalog.map((opt, idx) => (
                                    <div key={idx} className="col-12">
                                        <motion.button
                                            whileHover={{ x: 3 }}
                                            whileTap={{ scale: 0.99 }}
                                            onClick={() => applyCatalogOption(opt)}
                                            className="w-100 text-start p-2 rounded-3 border-0 bg-white shadow-sm d-flex align-items-center justify-content-between transition-all"
                                        >
                                            <div className="min-w-0">
                                                <div className="fw-bold text-dark" style={{ fontSize: '0.8rem' }}>{opt.name}</div>
                                                <div className="x-small text-muted text-truncate" style={{ fontSize: '10px' }}>
                                                    {opt.choices.map(c => c.name).join(' • ')}
                                                </div>
                                            </div>
                                            <div className="bg-light rounded-pill p-1 px-2 fw-bold text-primary" style={{ fontSize: '10px' }}>
                                                ADD
                                            </div>
                                        </motion.button>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </ModalBody>

            {/* Exit Confirmation Modal */}
            <Modal isOpen={showExitConfirm} toggle={() => setShowExitConfirm(false)} centered size="sm">
                <div className="p-4 text-center">
                    <div className="mx-auto bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                        <AlertTriangle className="text-warning" size={28} />
                    </div>
                    <h6 className="fw-bold mb-2">Unsaved Changes</h6>
                    <p className="text-muted small mb-4">
                        You have unsaved changes. Are you sure you want to discard them and exit?
                    </p>
                    <div className="d-flex gap-2 justify-content-center">
                        <Button color="light" className="border px-4 btn-sm" onClick={() => setShowExitConfirm(false)}>Cancel</Button>
                        <Button color="danger" className="px-4 btn-sm" onClick={() => { setShowExitConfirm(false); onClose(); }}>Exit</Button>
                    </div>
                </div>
            </Modal>
        </Modal>
    );
};

export default AddItemDialog;
